import { useState, useEffect, useRef } from "react";
import { getBranding, updateBranding, uploadBrandingLogo, removeBrandingLogo } from "../lib/adminApi";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Check, Info, Upload, Trash2, ImageIcon } from "lucide-react";
import { toast } from "sonner";

// Sensible defaults for the report-specific fields so a fresh install still
// renders a polished PDF even before the admin visits this page.
const REPORT_FIELD_DEFAULTS = {
  footer_text: "PrimeS4 Consulting",
  report_confidentiality_notice: "Confidential · for client discussion only",
};

export default function BrandingPage() {
  const [data, setData] = useState(null);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    getBranding()
      .then((d) => setData({ ...REPORT_FIELD_DEFAULTS, ...(d || {}) }))
      .catch(() => {});
  }, []);

  const save = async () => {
    try {
      const result = await updateBranding(data);
      setData({ ...REPORT_FIELD_DEFAULTS, ...result });
      setMsg("Saved");
      setTimeout(() => setMsg(""), 2000);
    } catch { setMsg("Error saving"); }
  };

  if (!data) return <p className="text-sm text-[#6B7280]">Loading...</p>;

  const setField = (k) => (e) => setData({ ...data, [k]: e.target.value });

  const onLogoChanged = (next) => setData({ ...REPORT_FIELD_DEFAULTS, ...next });

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-bold text-[#111827]" style={{ fontFamily: "Chivo" }}>Branding Settings</h1>
        <Button onClick={save} className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs" data-testid="branding-save-btn">
          <Check className="w-3 h-3 mr-1" />Save Changes
        </Button>
      </div>
      {msg && <div className="mb-3 px-3 py-2 bg-[#E0F2FE] border border-[#025979]/20 rounded-sm text-xs text-[#025979]" data-testid="branding-save-msg">{msg}</div>}

      {/* ------------------------------------------------------------------
          Product branding — WarehouseX product mark used inside the
          authenticated app shell (sidebars + login screen). Kept separate
          from Platform / Partner branding so the parent (PrimeS4) and
          product (WarehouseX) marks are managed independently.
          ------------------------------------------------------------------ */}
      <div className="bg-white border border-[#E5E7EB] rounded-sm shadow-sm p-5 space-y-5 mb-5">
        <div>
          <h2 className="text-sm font-semibold text-[#111827]" style={{ fontFamily: "Chivo" }}>Dashboard Branding</h2>
          <p className="text-[11px] text-[#6B7280] mt-0.5">
            The Dashboard Logo appears in the top-left of every authenticated workspace (Admin, Demo, Consultant, Client).
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <LogoUploader
            kind="product"
            label="Dashboard Logo"
            helper="Used in the app sidebar and at the top of the login panel. Falls back to the &ldquo;WarehouseX&rdquo; wordmark when empty."
            dataUrl={data.product_logo}
            onChanged={onLogoChanged}
          />
        </div>
      </div>

      {/* ------------------------------------------------------------------
          Platform branding — uploaded logos + primary/secondary colors
          ------------------------------------------------------------------ */}
      <div className="bg-white border border-[#E5E7EB] rounded-sm shadow-sm p-5 space-y-5 mb-5">
        <div>
          <h2 className="text-sm font-semibold text-[#111827]" style={{ fontFamily: "Chivo" }}>Platform branding</h2>
          <p className="text-[11px] text-[#6B7280] mt-0.5">
            Logos are uploaded as image assets. The Partner Logo, when present, is used on client-facing surfaces (Client View, PDF report) in place of the Platform Logo.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <LogoUploader
            kind="platform"
            label="Platform Logo"
            helper="Shown in the product header and on client-facing views by default."
            dataUrl={data.platform_logo}
            onChanged={onLogoChanged}
          />
          <LogoUploader
            kind="partner"
            label="Partner Logo (optional)"
            helper="Takes precedence over the Platform Logo on client-facing surfaces when uploaded."
            dataUrl={data.partner_logo}
            onChanged={onLogoChanged}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-medium text-[#374151] mb-1 block">Primary Color</label>
            <div className="flex gap-2 items-center">
              <input type="color" value={data.default_primary_color || "#025979"} onChange={setField("default_primary_color")} className="w-8 h-8 rounded-sm border" />
              <Input value={data.default_primary_color || ""} onChange={setField("default_primary_color")} className="rounded-sm text-xs flex-1" />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-[#374151] mb-1 block">Secondary Color</label>
            <div className="flex gap-2 items-center">
              <input type="color" value={data.default_secondary_color || "#03738C"} onChange={setField("default_secondary_color")} className="w-8 h-8 rounded-sm border" />
              <Input value={data.default_secondary_color || ""} onChange={setField("default_secondary_color")} className="rounded-sm text-xs flex-1" />
            </div>
          </div>
        </div>
      </div>

      {/* ------------------------------------------------------------------
          Report (PDF) branding — every string that appears in the
          Playwright-rendered Executive Summary PDF.
          ------------------------------------------------------------------ */}
      <div className="bg-white border border-[#E5E7EB] rounded-sm shadow-sm p-5 space-y-4">
        <div className="flex items-start justify-between gap-2">
          <div>
            <h2 className="text-sm font-semibold text-[#111827]" style={{ fontFamily: "Chivo" }}>Report (PDF) branding</h2>
            <p className="text-[11px] text-[#6B7280] mt-0.5">Controls every text string in the generated Executive Summary PDF.</p>
          </div>
          <span className="text-[10px] text-[#9CA3AF] inline-flex items-center gap-1">
            <Info className="w-3 h-3" /> Live on next download
          </span>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-medium text-[#374151] mb-1 block">Footer — Consulting Firm</label>
            <Input value={data.footer_text || ""} onChange={setField("footer_text")} className="rounded-sm text-xs" placeholder="PrimeS4 Consulting" data-testid="branding-footer-text" />
            <p className="text-[10px] text-[#9CA3AF] mt-1">Appears as “Prepared for {"{Client}"} · {"{this value}"}” at the bottom.</p>
          </div>
          <div>
            <label className="text-xs font-medium text-[#374151] mb-1 block">Footer — Confidentiality Notice</label>
            <Input value={data.report_confidentiality_notice || ""} onChange={setField("report_confidentiality_notice")} className="rounded-sm text-xs" placeholder="Confidential · for client discussion only" data-testid="branding-report-confidentiality" />
            <p className="text-[10px] text-[#9CA3AF] mt-1">The year is appended automatically ({new Date().getFullYear()}).</p>
          </div>
        </div>
      </div>

      <p className="text-[10px] text-[#9CA3AF] mt-4">PrimeS4 is the default branding. White-label partner branding overrides the Platform Logo on client-facing surfaces when uploaded.</p>
    </div>
  );
}

function LogoUploader({ kind, label, helper, dataUrl, onChanged }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const pick = () => fileRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!["image/png", "image/jpeg", "image/jpg", "image/svg+xml"].includes(file.type)) {
      toast.error("Only PNG, JPG, or SVG images are supported.");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Logo must be under 2 MB.");
      return;
    }
    setBusy(true);
    try {
      const next = await uploadBrandingLogo(kind, file);
      onChanged(next);
      toast.success(`${label} uploaded`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const onRemove = async () => {
    setBusy(true);
    try {
      const next = await removeBrandingLogo(kind);
      onChanged(next);
      toast.message(`${label} removed`);
    } catch {
      toast.error("Remove failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid={`branding-logo-${kind}`}>
      <label className="text-xs font-medium text-[#374151] mb-1.5 block">{label}</label>
      <div className="border border-dashed border-[#D1D5DB] rounded-sm p-4 bg-[#FAFBFC] flex items-center gap-4">
        <div
          className="w-20 h-20 bg-white border border-[#E5E7EB] rounded-sm flex items-center justify-center shrink-0 overflow-hidden"
          data-testid={`branding-logo-preview-${kind}`}
        >
          {dataUrl ? (
            <img loading="lazy" src={dataUrl} alt={`${label} preview`} className="max-w-full max-h-full object-contain" />
          ) : (
            <ImageIcon className="w-6 h-6 text-[#9CA3AF]" strokeWidth={1.5} />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              onClick={pick}
              disabled={busy}
              className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs h-8"
              data-testid={`branding-logo-upload-${kind}`}
            >
              <Upload className="w-3 h-3 mr-1" />
              {dataUrl ? "Replace" : "Upload"}
            </Button>
            {dataUrl && (
              <Button
                type="button"
                variant="outline"
                onClick={onRemove}
                disabled={busy}
                className="rounded-sm text-xs h-8 border-[#E5E7EB] text-[#6B7280] hover:bg-gray-50"
                data-testid={`branding-logo-remove-${kind}`}
              >
                <Trash2 className="w-3 h-3 mr-1" />
                Remove
              </Button>
            )}
          </div>
          <p className="text-[10px] text-[#9CA3AF] mt-2 leading-snug">{helper}</p>
          <p className="text-[10px] text-[#9CA3AF] mt-1">PNG, JPG, or SVG · up to 2 MB.</p>
        </div>
        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/svg+xml"
          onChange={onFile}
          className="hidden"
          data-testid={`branding-logo-input-${kind}`}
        />
      </div>
    </div>
  );
}
